import * as React from "react";
import Confetti from 'react-dom-confetti';

import { Deck, Hand, Card, Suit } from "../playing-cards";
import { sortCards, getAllSuits, SUIT_IMAGES } from "../playing-cards/Suit";
import * as storage from "../core/storage";

import { CardComponent } from "./CardComponent";
import { getOpponents, images as opponentImages } from "../engine/Opponent";
import { ScoreboardComponent } from "./ScoreboardComponent";
import { Player } from "../engine/Player";
import { RoundData } from "../engine/RoundData";
import { EndRoundComponent } from "./EndRoundComponent";

type Meld = Card[];

export interface IBoardState {
    dealing: boolean,
    isVoid: boolean,

    cards: Meld,
    deck: Deck,
    hands: Hand[],
    players: Player[],

    dealer: number,
    currentPlayer: number,
    turn: number,

    isFirstTurn: boolean,

    needOrderCard?: boolean,
    drawingCard?: Card,
    isDrawingFromDiscardPile: boolean,

    discardedCard?: Card,
    discardNeeded?: boolean,

    knockingPlayer: number,
    ginPlayer: number,

    rounds: RoundData[],
    currentRound: number,

    endOfRound: boolean,
    endOfRoundDialog: boolean,
}

const OPPONENT_AI_TIMEOUT = 700;
const NEXT_ACTION_TIMEOUT = 1000;

const confettiConfig = {
  angle: 90,
  spread: 45,
  startVelocity: 45,
  elementCount: 50,
  decay: 0.9
};

const NUM_PLAYERS = 2;

interface MeldConflict {
    melds: Meld[],
    cards: Card[]
}

/**
 * Rules:
 *
 * KNOCK: When the value of unmatched cards is 10 or less, a player can KNOCK
 *        The player scores the value of his opponent's unmatched cards - his own unmatched cards
 *        (K/Q/J = 10 points), 2-10 = Face value, Ace = 1 point
 *
 * UNDERCUT: After a KNOCK, if the opponent has an equal or lesser amount of unmatched cards, it's considered an undercut
 *           Scores the difference of unmatched cards + 10 points
 *
 * GIN: If player has no unmatched cards on his hand
 *      The player scores the value of his opponents unmatched cards + 20 points for going GIN
 *
 * WIN: First player to reach 100 points win
 *
 * REDEALING: If the deck reaches 2 cards, the round is VOID and is re-dealt
 */

export class BoardComponent extends React.Component<any, IBoardState> {
    deck: Deck;

    dealer: number;
    players: Player[];

    // 2 hands
    hands: Hand[] = [ new Hand(1), new Hand(2)  ];
    rounds: RoundData[];
    currentRound: number;
    suitsPerRound: Suit[] = [];
    maxPlayedCards: number = 4;

    currentDiscardIndex: number = 0;

    constructor (props: any) {
        super(props);
    }

    public componentWillMount() {
        const cachedRounds: any = storage.getItem("rounds");
        if (cachedRounds) {
            this.rounds = JSON.parse(cachedRounds);
            this.players = this.rounds[0].players.map(player => {
                // re-set player team
                player.team = undefined;
                player.image = opponentImages[player.name];
                return player;
            });
            this.dealer = (Number(storage.getItem("dealer")) + 1) % NUM_PLAYERS;
            this.currentRound = Number(storage.getItem("currentRound") || "0");

        } else {
            // create all players
            this.players = getOpponents(1).map(player => {
                player.score = 0;
                return player;
            });
            this.players.unshift({ name: "you" } as Player);
            this.rounds = [];
            // this.dealer = Math.floor(Math.random() * 2);
            this.dealer = 0;
            this.currentRound = -1;
        }

        this.startRound();
    }

    startRound () {
        this.deck = new Deck({
            shuffle: true,
            jokers: 0
        });

        this.currentDiscardIndex = 0;

        // make sure all hands have no cards.
        this.hands.forEach(hand => hand.clear());
        this.players.forEach(player => {
            player.balloon = false;
            player.balloonText = undefined;
            player.melds = [];
            player.deadwood = [];
        });

        this.setState({
            dealing: true,
            isVoid: false,
            isFirstTurn: false,
            dealer: this.dealer,
            currentPlayer: this.dealer,
            cards: this.deck.cards,
            hands: this.hands,
            players: this.players,
            rounds: this.rounds,
            turn: -1,
            knockingPlayer: -1,
            ginPlayer: -1,
            currentRound: this.currentRound,
            endOfRoundDialog: false,
            discardNeeded: false,
            drawingCard: undefined,
            discardedCard: undefined
        });

        //
        // animate card dealing process
        //
        setTimeout(() => {
            const dealStart = (this.dealer + 1) % NUM_PLAYERS;
            this.dealRound(dealStart, 1, () =>
                this.dealRound(dealStart, 1, () =>
                    this.dealRound(dealStart, 1, () =>
                        this.dealRound(dealStart, 1, () =>
                            this.dealRound(dealStart, 1, () =>
                                this.dealRound(dealStart, 1, () =>
                                    this.dealRound(dealStart, 1, () =>
                                        this.dealRound(dealStart, 1, () =>
                                            this.dealRound(dealStart, 1, () =>
                                                this.dealRound(dealStart, 1, () => {
                                                    this.setState({
                                                        dealing: false
                                                    }, () => {
                                                        this.nextTurn(false, { isFirstTurn: true });
                                                    })
                                                })
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            );
        }, 500);
    }

    dealCards = (hand: Hand, numCards: number, onComplete?: Function, timeout: number = 80) => {
        this.deck.deal(hand, numCards);

        this.setState({
            cards: this.deck.cards,
            hands: this.hands
        });

        this.computeRunsAndSets(this.state.hands.indexOf(hand));

        if (onComplete) {
            setTimeout(onComplete, timeout);
        }
    }

    dealRound = (startFrom: number, numCards: number, onComplete?: Function) => {
        let i=0;
        const getHandIndex = () => (startFrom + i) % NUM_PLAYERS;

        this.dealCards(this.hands[getHandIndex()], numCards, () => {
            i++;
            this.dealCards(this.hands[getHandIndex()], numCards, onComplete);
        });
    }

    onDiscardCard = (card: Card) => {
        const cardIndex = this.hands[this.state.currentPlayer].cards.indexOf(card);
        const discardedCard = this.hands[this.state.currentPlayer].discard(cardIndex, this.currentDiscardIndex++);

        // this.hands[this.state.currentPlayer].draw(this.state.drawingCard as Card);

        this.computeRunsAndSets(this.state.currentPlayer);

        const nextState = {
            drawingCard: undefined,
            isDrawingFromDiscardPile: false,
            discardNeeded: false,
            hands: this.state.hands,
            players: this.players,
            cards: this.deck.cards,
            discardedCard: discardedCard,
            isFirstTurn: false,
        };

        this.setState(nextState, () => this.nextTurn());
    }

    onPass = () => {
        const player = this.players[this.state.currentPlayer];
        player.balloon = true;
        player.balloonText = "I'll pass! 👎";

        const isCurrentPlayer = (this.state.currentPlayer === 0);

        if (isCurrentPlayer) {
            this.nextTurn(true, { isFirstTurn: false }, false);

        } else {
            this.setState({
                players: this.players
            }, () => {
                setTimeout(() => this.nextTurn(true, { isFirstTurn: false }), NEXT_ACTION_TIMEOUT);
            });
        }
    }

    onDiscardDrawingCard = (nextTurn: boolean = true) => {
        const discardedCard = this.state.drawingCard as Card;

        const hand = this.hands[this.state.currentPlayer];
        hand.discard(hand.cards.indexOf(discardedCard), this.currentDiscardIndex++);

        this.computeRunsAndSets();

        this.setState({
            cards: this.deck.cards,
            drawingCard: undefined,
            discardedCard: discardedCard,
            discardNeeded: false,
            needOrderCard: false,
            isDrawingFromDiscardPile: false,
            isFirstTurn: false,
        }, (nextTurn) ? () => this.nextTurn() : undefined);
    }

    onDrawSelected (card: Card, callback?: any, nextState: any = {}) {
        const isDrawingFromDiscardPile = (card === this.state.discardedCard)

        if (isDrawingFromDiscardPile) {
            card.discardIndex = undefined;
        }

        this.hands[this.state.currentPlayer].draw(card);
        this.computeRunsAndSets(this.state.currentPlayer);

        nextState.cards = this.deck.cards;
        nextState.drawingCard = card;
        nextState.discardedCard = undefined;
        nextState.needOrderCard = false;
        nextState.isDrawingFromDiscardPile = isDrawingFromDiscardPile;

        if (nextState.discardNeeded === undefined) {
            nextState.discardNeeded = true;
        }

        this.setState(nextState, typeof(callback) === "function" ? callback : undefined);
    }

    onGin = () => {
        console.log("GIN!")

        const player = this.players[this.state.currentPlayer];
        player.balloon = true;
        player.balloonText = "Gin! 🍸";

        this.onEndOfRound({
            ginPlayer: this.state.currentPlayer
        });
    }

    onKnock = () => {
        console.log("KNOCK!");

        const player = this.players[this.state.currentPlayer];
        player.balloon = true;
        player.balloonText = "Knock! ✊";

        this.onEndOfRound({
            knockingPlayer: this.state.currentPlayer
        });
    }

    /**
     * Check if opponent can lay off his deadwood into knocking player's melds
     */
    checkLayOff = () => {
        const opponentHand = this.hands[(this.state.knockingPlayer + 1) % NUM_PLAYERS];

        // distribute deadwoods from HIGHER to LOWER
        const deadwood = sortCards(this.players[(this.state.knockingPlayer + 1) % NUM_PLAYERS].deadwood, { asc: false });

        const melds = this.players[this.state.knockingPlayer].melds;
        const hand = this.state.hands[this.state.knockingPlayer];

        const addCardToPlayerHand = (card: Card) => {
            card.player = this.state.knockingPlayer;
            opponentHand.discard(opponentHand.cards.indexOf(card));
            hand.draw(card);
        }

        const filterMeldsForCard = (card: Card) => {
            melds.filter(meld => {
                const cardFits = this.checkCardFitsInMeld(card, meld);

                if (cardFits) {
                    addCardToPlayerHand(card);
                }

                return cardFits;
            }).length === 0;
        }

        const remaining = deadwood.filter(card => filterMeldsForCard(card));
        const hasLaidOff = (remaining.length !== deadwood.length);

        if (hasLaidOff) {
            // pass twice to ensure cards in-between are placed as well.
            remaining.forEach(card => filterMeldsForCard(card));
        }

        this.computeRunsAndSets(this.state.knockingPlayer);
        this.computeRunsAndSets((this.state.knockingPlayer + 1) % NUM_PLAYERS);

        this.setState({
            hands: this.state.hands,
            players: this.players,
            cards: this.deck.cards,
        });

        return hasLaidOff;
    }

    checkCardFitsInMeld (card: Card, meld: Meld, allowedDifference: number = 1) {
        if (
            meld[0] && meld[1] &&
            meld[0].number === meld[1].number &&
            meld.length < 4
        ) {
            // meld is a SET
            if (card.number === meld[0].number) {
                return true;
            }

        } else if (card.suit === meld[0].suit && meld.length < 5) {
            // meld is a RUN
            if (
                Math.abs((card.number as number) - (meld[0].number as number)) <= allowedDifference ||
                Math.abs((card.number as number) - (meld[meld.length - 1].number as number)) <= allowedDifference
            ) {
                return true;
            }
        }

        return false;
    }

    onPlayAnotherHand = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        // clean-up player data before storing it
        this.rounds.forEach(round => {
            round.players.forEach(player => {
                player.balloon = undefined;
                player.balloonText = undefined;
                player.image = undefined;
            });
        });

        storage.setItem("rounds", JSON.stringify(this.rounds));
        storage.setItem("dealer", JSON.stringify(this.dealer));
        storage.setItem("currentRound", JSON.stringify(this.state.currentRound));
        window.location.reload();
    }

    onResetScores = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        storage.removeItem("rounds");
        storage.removeItem("dealer");
        storage.removeItem("currentRound");
        window.location.reload();
    }

    nextTurn (changeCurrentPlayer: boolean = true, nextState: IBoardState & any = {}, removeBalloons: boolean = true) {
        const numCardsInDeck = this.deck.cards.filter(card => {
            return card.player === undefined && card.discardIndex === undefined;
        }).length;

        if (numCardsInDeck <= 2) {
            this.setState({
                isVoid: true
            }, () => {
                setTimeout(() => this.startRound(), NEXT_ACTION_TIMEOUT);
            });
            return;
        }

        // reset player balloon
        nextState.players = this.state.players;

        if (removeBalloons) {
            for (let playerNum in nextState.players) {
                nextState.players[playerNum].balloon = false;
            }
        }

        nextState.turn = this.state.turn + 1;
        nextState.currentPlayer = (changeCurrentPlayer)
            ? (this.state.currentPlayer + 1) % NUM_PLAYERS
            : this.state.currentPlayer;

        if (nextState.currentPlayer === 0) {
            nextState.needOrderCard = true;
        }

        if (this.state.turn === -1) {
            const discardedCard = this.deck.draw();
            discardedCard.discardIndex = this.currentDiscardIndex++;

            nextState.discardedCard = discardedCard;
        }

        if (nextState.currentPlayer !== 0) {
            //
            // Let AI act after a timeout.
            //
            setTimeout(() => this.actAI(), OPPONENT_AI_TIMEOUT);
        }

        if (nextState.turn === 0) {
            this.setState(this.onRoundStart(nextState));

        } else {
            this.setState(nextState);
        }
    }

    onRoundStart (nextState: IBoardState & any) {
        if (nextState.currentPlayer === undefined) {
            nextState.currentPlayer = (this.state.dealer + 1) % NUM_PLAYERS;
        }

        const newRound = new RoundData(nextState.players);
        nextState.currentRound = this.state.currentRound + 1;
        nextState.rounds = this.rounds;
        nextState.rounds.push(newRound);
        return nextState;
    }

    onEndOfRound (nextState: any = {}) {
        if (this.state.discardNeeded) {

            // auto-discard highest deadwood card.
            const deadwood = this.players[this.state.currentPlayer].deadwood;
            if (deadwood.length > 0) {
                const highestDeadwood = sortCards(deadwood, { asc: false })[0];

                const cardIndex = this.hands[this.state.currentPlayer].cards.indexOf(highestDeadwood);
                this.hands[this.state.currentPlayer].discard(cardIndex, this.currentDiscardIndex++);
                // const discardedCard = this.hands[this.state.currentPlayer].discard(cardIndex, this.currentDiscardIndex++);
            }

            this.computeRunsAndSets(this.state.currentPlayer);
        }

        nextState.cards = this.deck.cards;
        nextState.players = this.players;
        nextState.discardNeeded = false;
        nextState.drawingCard = undefined;
        nextState.discardedCard = undefined;
        nextState.currentPlayer = (this.state.currentPlayer + 1) % NUM_PLAYERS;

        this.setState(nextState);

        setTimeout(() => {
            this.setState({ endOfRound: true });

            setTimeout(() => {
                const isKnock = (this.state.knockingPlayer !== -1);
                let computeEndOfRoundTimeout = OPPONENT_AI_TIMEOUT;

                if (isKnock) {
                    if (this.checkLayOff()) {
                        // only increase end of round timeout if a layoff has occurred.
                        computeEndOfRoundTimeout *= 1.5;
                    }
                }

                setTimeout(() => this.setState(this.computeEndOfRound()), computeEndOfRoundTimeout);
            }, NEXT_ACTION_TIMEOUT);
        }, NEXT_ACTION_TIMEOUT);
    }

    computeEndOfRound (nextState: IBoardState & any = {}) {
        const currentRound = this.state.rounds[this.state.currentRound];
        const isKnock = (this.state.knockingPlayer !== -1);

        const playerAnnouncing = (isKnock)
            ? this.state.knockingPlayer
            : this.state.ginPlayer;
        const opponentIndex = (playerAnnouncing + 1) % NUM_PLAYERS;

        const knockingDeadwood = this.getDeadwoodScore(this.players[playerAnnouncing].deadwood);
        const opponentsDeadwood = this.getDeadwoodScore(this.players[opponentIndex].deadwood);

        let winningPlayer: number = playerAnnouncing;

        // KNOCK SCORE
        let score = opponentsDeadwood - knockingDeadwood;
        let result = "KNOCK";

        if (isKnock && score <= 0) {
            // UNDERCUT!
            winningPlayer = opponentIndex;
            score = knockingDeadwood - opponentsDeadwood + 10;
            result = "UNDERCUT";
        }

        if (!isKnock) {
            // GIN BONUS
            score += 20;
            result = "GIN";
        }

        currentRound.winner = winningPlayer;
        currentRound.score = score;
        currentRound.result = result;

        nextState.endOfRoundDialog = true;
        nextState.currentPlayer = undefined;
        nextState.rounds = this.state.rounds;

        // remove player ballons
        nextState.players = this.state.players;
        for (let playerNum in this.state.players) {
            nextState.players[playerNum].balloon = false;
        }

        return nextState;
    }

    getWinnerCard (cards: Meld) {
        return cards[0];
    }

    checkTrickWinner (cards: Meld, apply: boolean = true) {
        const winner = this.getWinnerCard(cards).player;

        return winner as number;
    }

    actAI () {
        console.log("ACT AI!");
        const opponent = this.state.players[this.state.currentPlayer];
        const discardedCard = this.state.discardedCard as Card;

        let { runs, sets } = this.computeRunsAndSets(this.state.currentPlayer);

        const getDeadwoodRanking = (card: Card) => {
            const inDirectRuns = runs.
                filter(run => run.length >= 2 && run.length < 5).
                filter(run => run.indexOf(card) !== -1 || this.checkCardFitsInMeld(card, run, 1)).
                length;

            const inPossibleRuns = runs.
                filter(run => run.length >= 2 && run.length < 5).
                filter(run => run.indexOf(card) !== -1 || this.checkCardFitsInMeld(card, run, 2)).
                length;

            const inSet = Number(
                sets[card.number as number] &&
                sets[card.number as number].length >= 2 &&
                sets[card.number as number].length < 4
            );

            let ranking = (inDirectRuns * 2) + (inPossibleRuns - inDirectRuns) + (inSet);

            // higher cards without possible meld have higher priority for discarding
            if (ranking === 0) {
                ranking -= card.number as number;
            }

            // console.log("inDirectRuns:", inDirectRuns, "inPossibleRuns:", inPossibleRuns, "inSet:", inSet, "ranking:", ranking);
            return ranking;
        };

        const actionAfterDraw = () => {
            // compute runs and sets again
            const runsAndSets = this.computeRunsAndSets(this.state.currentPlayer);
            runs = runsAndSets.runs;
            sets = runsAndSets.sets;

            setTimeout(() => {
                if (opponent.deadwood.length <= 1) {
                    this.onGin();

                } else if (this.getDeadwoodScore(opponent.deadwood, true) <= 10) {
                    this.onKnock();

                } else {
                    const deadwoodByRank = opponent.deadwood.slice(0).sort((a,b) => {
                        const rankA = getDeadwoodRanking(a);
                        const rankB = getDeadwoodRanking(b);
                        return (rankA > rankB)
                            ? 1
                            : (rankA === rankB)
                                ? 0
                                : -1;
                    });

                    this.onDiscardCard(deadwoodByRank[0]);
                }
            }, OPPONENT_AI_TIMEOUT);
        };

        /**
         * simulate discarded card in hand,
         * to check if it's going to be discarded for having low ranking
         */
        this.state.hands[this.state.currentPlayer].cards.push(discardedCard);
        const runsAndSets = this.computeRunsAndSets(this.state.currentPlayer);
        runs = runsAndSets.runs;
        sets = runsAndSets.sets;

        const deadwoodByRank = opponent.deadwood.sort((a, b) => {
            a.score = getDeadwoodRanking(a);
            b.score = getDeadwoodRanking(b);
            return (a.score > b.score)
                ? 1
                : (a.score === b.score)
                    ? 0
                    : -1;
        });

        /**
         * (revert simulation)
         * remove discarded card from opponent's deadwood
         */
        this.state.hands[this.state.currentPlayer].cards = this.state.hands[this.state.currentPlayer].cards.filter(card => card !== discardedCard);
        this.computeRunsAndSets(this.state.currentPlayer)

        // discarded card may be in a meld and not found on deadwood list
        const discardedCardScore = deadwoodByRank.filter(card => card === discardedCard)[0];
        if (
            deadwoodByRank[0] !== discardedCard && (
                !discardedCardScore ||
                discardedCardScore.score > 1
            )
        ) {
            this.onDrawSelected(discardedCard, actionAfterDraw);

        } else if (this.state.isFirstTurn) {
            this.onPass();

        } else {
            this.onDrawSelected(this.deck.draw(), actionAfterDraw);
        }
    }

    getPreviousPlayer () {
        return (4 + (this.state.currentPlayer - 1)) % 4;
    }

    computeRunsAndSets (player: number = this.state.currentPlayer) {
        let runsPartial: Meld[] = [];
        let sets: Meld[] = [];
        let setsByNumber: {[number: number]: Meld} = {};

        const cards = sortCards(this.state.hands[player].cards, {
            asc: true,
            suits: getAllSuits()
        });

        // Retrieve all SETS
        cards.forEach((card) => {
            const number = card.number as number;
            if (!setsByNumber[number]) {
                setsByNumber[number] = [];
            }
            setsByNumber[number].push(card);
        });

        for (let number in setsByNumber) {
            if (setsByNumber[number].length >= 3) {
                sets.push(setsByNumber[number]);

            }

            /**
             * When a set has 4 cards, all other 3 card combinations should be considered
             * for conflict resolution
             */
            if (setsByNumber[number].length === 4) {
                setsByNumber[number].forEach(cardToExclude => {
                    sets.push(setsByNumber[number].filter(card => card !== cardToExclude));
                });
            }
        }

        // Retrieve all RUNS
        cards.forEach((card) => {
            let run = runsPartial.find(cards =>
                cards.filter(cardInRun =>
                    cardInRun.suit === card.suit && Math.abs((cardInRun.number as number) - (card.number as number)) === 1
                ).length > 0
            );
            if (run) {
                run.push(card);
            } else {
                runsPartial.push([card]);
            }
        });

        // split runs higher than 5 into pairs of threes
        runsPartial = runsPartial.reduce<Meld[]>((runs, meld: Meld) => {
            if (meld.length > 3) {
                if (meld.length === 5) {
                    runs.push(meld);
                }

                for (let offset = 0; offset <= meld.length - 3; offset++) {
                    const possibleMeld = meld.slice(offset, offset + 3);
                    runs.push(possibleMeld);
                }

                for (let offset = 0; offset <= meld.length - 4; offset++) {
                    const possibleMeld = meld.slice(offset, offset + 4);
                    runs.push(possibleMeld);
                }
            } else if (meld.length === 3) {
                runs.push(meld);
            }

            return runs;
        }, []);

        const runs = runsPartial.
            map((meld) => sortCards(meld, { asc: true, suits: getAllSuits() }));

        const melds = runs.concat(sets);

        const hasConflict = (meldA: Meld, meldB: Meld) =>
            meldA.find(card => meldB.indexOf(card) !== -1) !== undefined;

        // Get conflicting melds
        const meldConflicts = melds.reduce((result, meldA) => {
            for (let i = 0, l = melds.length; i < l; i++) {
                const meldB = melds[i];

                if (meldA === meldB) {
                    continue;
                }

                if (hasConflict(meldA, meldB)) {
                    let existingConflict = result.find(previousConflict => (
                        previousConflict.cards.find((card: Card) => {
                            return meldA.indexOf(card) !== -1;
                        }) !== undefined
                    ));

                    let conflict: MeldConflict = existingConflict || {
                        melds: [],
                        cards: []
                    };

                    if (!existingConflict) {
                        result.push(conflict);
                    }

                    // push only unique conflict
                    if (conflict.melds.indexOf(meldB) === -1) {
                        conflict.melds.push(meldB);

                        conflict.melds.sort((meldA, meldB) => {
                            const meldAScore = this.getDeadwoodScore(meldB);
                            const meldBScore = this.getDeadwoodScore(meldA);
                            return (meldAScore > meldBScore)
                                ? -1
                                : (meldAScore < meldBScore)
                                    ? 1
                                    : 0;
                        });

                        for (let card of meldB) {
                            // push unique cards for further conflict resolution
                            if (conflict.cards.indexOf(card) === -1) {
                                conflict.cards.push(card);
                            }
                        }
                    }
                }
            }

            return result;
        }, [] as MeldConflict[]);

        /**
         * Check conflicting melds
         */
        if (meldConflicts.length > 0) {
            meldConflicts.forEach((conflict) => {
                const possibleMelds = conflict.melds.map((meldA) => {
                    return conflict.melds.reduce((result: Meld[], meldB: Meld) => {
                        if (meldA !== meldB) {
                            let noConflicts = result.find((existingMeld) => hasConflict(existingMeld, meldB)) === undefined;
                            if (noConflicts) {
                                result.push(meldB);
                            }
                        }
                        return result;
                    }, [meldA]);
                });

                possibleMelds.sort((meldsA, meldsB) => {
                    return meldsB.reduce((score, meld) => {
                        score += (meld.length * 20) + this.getDeadwoodScore(meld);
                        return score;
                    }, 0) - meldsA.reduce((score, meld) => {
                        score += (meld.length * 20) + this.getDeadwoodScore(meld);
                        return score;
                    }, 0);
                });

                const acceptedMelds = possibleMelds.shift(); // const acceptedMelds =
                possibleMelds.forEach(excludedMelds => {
                    excludedMelds.forEach(excludedMeld => {
                        const index = melds.indexOf(excludedMeld);
                        if (index !== -1) {
                            melds.splice(index, 1);
                        }
                    });
                });

                if (acceptedMelds && acceptedMelds.length > 0) {
                    acceptedMelds.forEach(meld => {
                        if (melds.indexOf(meld) === -1) {
                            melds.push(meld)
                        }
                    });
                }
            });
        }

        this.players[player].melds = melds;

        const cardsInMelds = melds.reduce((cards, meld) => {
            cards = cards.concat(...meld);
            return cards;
        }, []);

        const deadwood = this.state.hands[player].cards.filter(card => {
            return cardsInMelds.indexOf(card) === -1;
        });

        this.players[player].deadwood = sortCards(deadwood, {
            asc: true,
            suits: getAllSuits(),
        });

        return { runs: runsPartial, sets: setsByNumber };
    }

    getDeadwoodScore (cards: Meld, predictDiscard: boolean = false) {
        let reducePredicted = 0;

        if (predictDiscard && this.state.discardNeeded && cards.length > 0) {
            let highestCard = cards[0];
            for (let i=0, l=cards.length; i<l; i++) {
                if (Math.min((cards[i].number as number), 10) > Math.min((highestCard.number as number), 10)) {
                    highestCard = cards[i];
                }
            }
            reducePredicted = Math.min(highestCard.number as number, 10);
        }

        return cards.reduce((prev, curr) => prev + Math.min((curr.number as number), 10), 0) - reducePredicted;
    }

    getCardShortName (card: Card, i?: number) {
        let name: string;

        switch (card.number) {
            case 1: name = "A"; break;
            case 11: name = "J"; break;
            case 12: name = "Q"; break;
            case 13: name = "K"; break;
            default: name = (card.number as number).toString();
        }

        return <span key={i} className={`short-card suit-${card.suit}`}>
            <img src={SUIT_IMAGES[card.suit]} /> {name}
        </span>
    }

    public render () {
        const isCurrentPlayer = this.state.currentPlayer === 0;

        const hasOverlay = (
            (isCurrentPlayer && (this.state.discardedCard || this.state.discardNeeded)) ||
            this.state.isVoid
        );

        const deadwoodPredicted = this.getDeadwoodScore(this.state.players[0].deadwood, true);

        return <section className={`board`}>
            <ScoreboardComponent players={this.state.players} rounds={this.state.rounds} />

            <div className="confetti">
                <Confetti active={(this.state.endOfRoundDialog && this.state.rounds[this.state.currentRound].winner === 0)} config={ confettiConfig }/>
            </div>

            {(this.state.endOfRoundDialog)
                ? <EndRoundComponent
                    onPlayAnotherHand={this.onPlayAnotherHand}
                    onResetScores={this.onResetScores}
                    currentRound={this.state.currentRound}
                    rounds={this.state.rounds} />
                : null}

            {this.state.players.map((opponent, i) => (
                <div
                    key={`player-${i}`}
                    className={`
                        player
                        i-${i+1}
                        ${(this.state.dealer === i) ? 'dealer' : ''}
                        ${(this.state.currentPlayer === i) ? 'current' : ''}
                    `}>
                    { (opponent.image)
                        ? <img src={opponent.image} />
                        : null }
                    <span className={`balloon ${opponent.balloon ? "active" : ""}`}>{opponent.balloonText}</span>

                    <span className="name">
                        {opponent.name}
                        <span className={`team ${opponent.team}`}>{opponent.team}</span>
                        <span className="dealer">D</span>
                    </span>
                    {
                    (i === 0 || this.state.endOfRound)
                    // (true)
                        ?
                            <span className="tricks">
                                <div className="melds">
                                    <span>Melds:</span>
                                    { opponent.melds.map((meld, i) => (
                                        <span key={i} className="meld">{meld.map((card, i) => this.getCardShortName(card, i))}</span>
                                    )) }
                                </div>
                                {(this.getDeadwoodScore(opponent.deadwood) > 0)
                                    ?
                                        <div className="deadwood">
                                            <span>Deadwood ({this.getDeadwoodScore(opponent.deadwood)}):</span>
                                            <span className="meld">
                                                {opponent.deadwood.map((card, i) => this.getCardShortName(card, i))}
                                            </span>
                                        </div>
                                    :
                                        null }
                            </span>
                        : null }
                </div>
            ))}

            {/* drawing card */}
            <div className={`overlay ${hasOverlay ? "" : "inactive"}`} />
            <div className={`dialog-overlay ${hasOverlay ? "" : "inactive"}`}>
                <div className="dialog">
                    {
                        (this.state.isVoid)
                            ? <p>This round is void. A new round will be dealt.</p>
                            : (this.state.discardNeeded)
                                ? <p>Discard one card from your hand</p>
                                : (this.state.isFirstTurn)
                                    ? <p>Would you like to draw { this.getCardShortName(this.state.discardedCard as Card) } ?</p>
                                    : <p>Draw a card from the deck or discard pile</p>
                    }

                    {(deadwoodPredicted === 0 && this.state.drawingCard)
                        ? <button className="gin" onClick={this.onGin.bind(undefined)}>Gin 🍸</button>
                        : (deadwoodPredicted <= 10 && this.state.drawingCard)
                            ? <button className="knock" onClick={this.onKnock.bind(undefined)}>Knock ✊</button>
                            : undefined }

                    {(this.state.isFirstTurn && !this.state.drawingCard)
                        ? <button className="pass" onClick={this.onPass.bind(undefined)}>Pass 👎</button>
                        : (this.state.discardNeeded && this.state.drawingCard !== this.state.discardedCard)
                            ? <button className="pass discard" onClick={this.onDiscardDrawingCard.bind(undefined)}>Discard it 👎</button>
                            : undefined }
                </div>
            </div>

            {/* All cards */}
            {this.state.cards.map((card, i) => {
                const playerNum = (!isCurrentPlayer || card !== this.state.drawingCard)
                    ? card.player as number
                    : undefined;
                const player = playerNum !== undefined ? this.players[playerNum - 1] : undefined;
                const faceUp = (
                    (playerNum === 1) ||
                    (card === this.state.discardedCard) ||
                    (playerNum !== undefined && this.state.endOfRound)
                );
                const enabled = (
                    !this.state.endOfRound &&
                    player !== undefined &&
                    !player.played && (
                        this.state.discardNeeded || this.state.currentPlayer === 0
                    )
                );

                // on-click handler
                let onClick;
                if (
                    !this.state.dealing &&
                    !this.state.endOfRound &&
                    this.state.currentPlayer === 0 &&
                    card !== this.state.drawingCard
                ) {
                    if (this.state.discardNeeded) {
                        onClick = this.onDiscardCard.bind(this, card);

                    } else {
                        onClick = this.onDrawSelected.bind(this, card, undefined, undefined);
                    }
                }

                // class name
                let className = (playerNum && `player-${playerNum}`) || ('board ' + (this.state.dealing ? 'dealing' : ''));
                if (this.state.isVoid) {
                    className += " void";

                } else if (this.state.drawingCard === card) {
                    // only use 'drawing' class for current player
                    // the opponent will draw instantly to his hand.
                    if (isCurrentPlayer) {
                        className += " drawing"
                    }

                } else if (this.state.discardNeeded) {
                    className += " discard-needed";

                } else if (
                    this.state.needOrderCard && !(
                        // in the first-turn, user cannot draw from the deck
                        this.state.turn === 0 &&
                        card.discardIndex === undefined
                    )
                ) {
                    className += " need-order";
                }

                if (this.state.endOfRound) {
                    className += " end-of-round";
                }

                /**
                 * Sort cards in hand based on current melds
                 */
                if (playerNum) {
                    i = 0;

                    const melds = this.players[playerNum - 1].melds;

                    let cardsInMelds = 0;
                    let isInMeld = false;

                    // discover index of drawingCard on player's melds.
                    // this is used to decrease index in order to display cards in hand in proper order
                    let drawingCardIndex = 0;
                    let drawingCardMeldIndex = -1;
                    if (this.state.drawingCard) {
                        for (let j = 0; j < melds.length; j++) {
                            let index = melds[j].indexOf(this.state.drawingCard as Card)
                            if (index !== -1) {
                                drawingCardIndex += index;
                                drawingCardMeldIndex = j;
                                break;

                            } else {
                                drawingCardIndex += melds[j].length;
                            }
                        }
                    }
                    if (drawingCardMeldIndex === -1) {
                        drawingCardIndex = -1;
                    }

                    // loop through all melds to sort cards in hand based on them
                    for (let j=0; j<melds.length; j++) {
                        if (j > 0) {
                            i += melds[j-1].length;
                        }

                        for (let k=0; k<melds[j].length; k++) {
                            const cardInMeld = melds[j][k];

                            if (cardInMeld === card) {
                                i += k;
                                isInMeld = true;

                            } else if (cardInMeld !== this.state.drawingCard) {
                                cardsInMelds += 1;
                            }
                        }

                        // reduce number if index
                        if (
                            drawingCardIndex !== -1 &&
                            j >= drawingCardMeldIndex &&
                            i >= drawingCardIndex
                        ) {
                            i -= 1;
                            drawingCardIndex = -1; // prevent i-1 from happening more than once
                        }

                        if (isInMeld) {
                            break;
                        }
                    }

                    if (!isInMeld) {
                        const deadwood = this.players[playerNum - 1].deadwood.filter(card => card !== this.state.drawingCard);
                        i = cardsInMelds + deadwood.indexOf(card);
                    }
                }

                // prevent i from being less than zero
                if (i < 0) i = 5;

                return <CardComponent
                    player={playerNum}
                    key={`card-${card.suit}${card.number}`}
                    className={className}
                    faceUp={faceUp}
                    onClick={onClick}
                    enabled={enabled}
                    i={i}
                    {...card} />
            })}

        </section>;
    }

}



// WEBPACK FOOTER //
// ./src/components/BoardComponent.tsx